import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Label from 'reactstrap/lib/Label';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { AppraisalEntities, AppraisalModel, buildAllStylesAppraisalFilteredPath } from 'client/data/models/appraisal';
import { getIfEvTaxCreditAmountPath, IncentiveModel } from 'client/data/models/incentives';
import { FeatureSpecsEntities, VehicleEntities } from 'client/data/models/vehicle-v2';
import { INCENTIVE_TYPES } from 'site-modules/shared/constants/incentives';
import { getDealsUrl, getEvIncentivesUrl } from 'site-modules/shared/utils/core-link-constructor';
import { FeaturesSpecsParams } from 'site-modules/shared/utils/features-specs/params';
import { getDropdownItems, getStyleName, getUsedTmvRetail } from 'site-modules/shared/utils/features-specs/utils';
import { getPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import { withTargetText } from 'site-modules/shared/utils/url-utils';
import { isDiscontinuedDate } from 'site-modules/shared/utils/vehicle-utils';
import { FancyLabel } from 'site-modules/shared/components/custom-dropdown-label/custom-dropdown-label';
import { useIsClient } from 'site-modules/shared/hooks/use-is-client';
import { Link } from 'site-modules/shared/components/link/link';
import { StyledSelect } from 'site-modules/shared/components/styled-select/styled-select';
import { TaxRebateText } from './tax-rebate-text/tax-rebate-text';

import './features-trim-box.scss';

export function FeaturesTrimBoxUI({
  params,
  styleOverview,
  popularStyles,
  appraisalStyles = null,
  taxRebateAmount = null,
  className = null,
  onStyleChange = undefined,
}) {
  const isClient = useIsClient();
  const isDropdownRender = !params.styleId || isClient;
  const {
    styleAttributes: { electric, pluginElectric },
  } = styleOverview;

  const isElectric = electric || pluginElectric;

  const options = useMemo(
    () => getDropdownItems(popularStyles, params.isUsed ? appraisalStyles || [] : null),
    [appraisalStyles, params.isUsed, popularStyles]
  );

  const styleName = getStyleName(styleOverview.name);
  const displayPrice = params.isUsed
    ? getUsedTmvRetail(appraisalStyles, styleOverview.id)
    : styleOverview?.price?.baseMSRP;
  const currentStyle = popularStyles.find(({ id }) => id === get(styleOverview, 'id'));
  const isDiscontinued = !params.isUsed && isDiscontinuedDate(get(currentStyle, 'styleEndDate'));
  const isMostPopular = !isDiscontinued && get(styleOverview, 'id') === get(popularStyles, '[0].id');

  const hasLabel = isDiscontinued || isMostPopular;
  const labelProps = isDiscontinued && {
    highlightText: (
      <Fragment>
        <sup>*</sup>Discontinued
      </Fragment>
    ),
    highlightTextClasses: 'small fst-italic',
  };

  return (
    <div className={classnames('features-trim-box bg-white px-1 py-1 rounded-12', className)}>
      <div className="label-wrapper mb-1">
        {isDropdownRender && (
          <Label for="fs-style" className="heading-6 text-cool-gray-30 mb-0">
            Select a trim
          </Label>
        )}
      </div>
      <Row className="align-items-center">
        <Col xs={12} className="dropdown-wrapper mb-0_75">
          {isDropdownRender && (
            <div className="style-select-dropdown pos-r">
              <StyledSelect
                id="fs-style"
                value={get(styleOverview, 'id')}
                onChange={onStyleChange}
                name="select-style"
                valueKey="styleId"
                labelKey="name"
                wrapperClassName="w-100"
                overrideClasses={classnames(
                  'style-select d-block w-100 ps-1 pe-2 pb-0_75 bg-white text-truncate size-16 rounded-8',
                  {
                    'mb-0_75 mb-md-0 pt-0_75 pt-md-1_5': !hasLabel,
                    'pt-1_5': hasLabel,
                  }
                )}
                options={options}
                data-tracking-id="fs_model_select"
              />
              <div
                role="presentation"
                className="pos-a text-truncate bg-white"
                style={{
                  pointerEvents: 'none',
                  left: '1rem',
                  right: '2rem',
                  bottom: '0.75rem',
                }}
              >
                <FancyLabel
                  primaryText={styleName.style}
                  secondaryText={styleName.engine}
                  primaryTextClasses="heading-5"
                  secondaryTextClasses="field medium"
                  highlightTextClasses="text-blue-20 small fw-medium"
                  showHighlightText={hasLabel}
                  {...labelProps}
                />
              </div>
            </div>
          )}
        </Col>
        <Col xs={12} className="text-center text-md-start">
          {!!displayPrice && (
            <Fragment>
              <div
                className={classnames('d-flex align-items-baseline', {
                  'mb-0_25': isElectric,
                })}
              >
                <div className="small fw-medium me-0_25">{params.isUsed ? 'Est. Market Value:' : 'Starting MSRP:'}</div>
                <div className="fw-bold">{getPriceStringV2(parseInt(displayPrice, 10))}</div>
              </div>
              {isElectric && (
                <div className="d-flex flex-wrap small">
                  <span className="fw-medium me-0_25">Federal EV Tax Credit and Rebates:</span>
                  <span className="me-0_25">
                    <TaxRebateText taxRebateAmount={taxRebateAmount} />
                  </span>

                  {taxRebateAmount !== null && (
                    <Link
                      to={
                        params.isUsed
                          ? getEvIncentivesUrl({
                              makeName: currentStyle?.makeName,
                              modelName: currentStyle?.modelName,
                              vehCondition: 'USED',
                              incentiveType: 'Tax Credit',
                            })
                          : withTargetText(
                              getDealsUrl(
                                { makeSlug: params.make, modelSlug: params.model, year: params.year },
                                taxRebateAmount
                                  ? { styleId: styleOverview.id, section: INCENTIVE_TYPES.OTHER }
                                  : undefined
                              ),
                              { text: 'federal credit' }
                            )
                      }
                      rel={params.isUsed ? 'nofollow' : undefined}
                      className="text-blue-40 text-underline"
                      data-tracking-id="view_ev_tax_credit"
                    >
                      {taxRebateAmount ? 'Based on income' : 'See other incentives'}
                      <i className="icon-arrow-right3 disclaimer ms-0_25" aria-hidden />
                    </Link>
                  )}
                </div>
              )}
            </Fragment>
          )}
        </Col>
      </Row>
    </div>
  );
}

FeaturesTrimBoxUI.propTypes = {
  params: FeaturesSpecsParams.isRequired,
  styleOverview: FeatureSpecsEntities.FeatureStyleOverviewEntity.isRequired,
  popularStyles: VehicleEntities.PopularStyles.isRequired,
  appraisalStyles: AppraisalEntities.Styles,
  taxRebateAmount: PropTypes.number,
  className: PropTypes.string,
  onStyleChange: PropTypes.func,
};

export const propsAreEqual = (prevProps, { appraisalStyles, params, taxRebateAmount, styleOverview }) =>
  taxRebateAmount === null &&
  prevProps?.styleOverview?.id === styleOverview?.id &&
  prevProps?.appraisalStyles === appraisalStyles &&
  prevProps?.params?.isUsed === params?.isUsed;

export const FeaturesTrimBox = connectToModel(React.memo(FeaturesTrimBoxUI, propsAreEqual), {
  appraisalStyles: bindToPath(
    ({ params }) => params.isUsed && buildAllStylesAppraisalFilteredPath(params),
    AppraisalModel
  ),
  taxRebateAmount: bindToPath(
    ({ params, styleOverview }) => getIfEvTaxCreditAmountPath(styleOverview.id, params.isUsed),
    IncentiveModel
  ),
});
